import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineArrowRight, HiOutlineArrowPath, HiOutlineXMark } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import { isChatGptGrant } from '../../../utils/mcpClients';
import useOAuthGrants from '../../../hooks/useOAuthGrants';
import { ChatGptMark } from '../../ConnectClaude/connectClients';
import ChatGptSetupSteps from '../../../components/ChatGptSetupSteps';
import ConnectedClientRow from './ConnectedClientRow';
import { CardBodySkeleton } from './CardSkeleton';

// The "ChatGPT" integration card — sibling of the Claude card, for the same
// reason: ChatGPT signs in rather than using a token, so filing it under the
// token-based MCP card would send people hunting for a token they never need.

const SetupModal = ({ isOpen, onClose, mcpUrl }) => {
  if (!isOpen) return null;

  // Portal to <body>: the cards use backdrop-blur, and a filtered ancestor becomes
  // the containing block for position:fixed (same fix as ConnectClaudeModal).
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-200/80 dark:border-gray-800 flex items-center gap-3">
          <ChatGptMark className="w-8 h-8 flex-shrink-0" rounded="rounded-lg" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">Connect ChatGPT</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">No token needed — you sign in</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 dark:bg-gray-900/50">
          <ChatGptSetupSteps mcpUrl={mcpUrl} />
        </div>
      </div>
    </div>,
    document.body
  );
};

const ChatGptIntegrationCard = () => {
  const { grants, loading, reload, removeGrant } = useOAuthGrants(isChatGptGrant);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const handleDisconnect = async (grantId) => {
    setDisconnecting(grantId);
    setError(null);
    try {
      await cloudApi.revokeOAuthGrant(grantId);
      removeGrant(grantId);
    } catch (err) {
      setError(err.message || 'Failed to disconnect.');
    } finally {
      setDisconnecting(null);
    }
  };

  // Connecting happens inside ChatGPT, so re-check on close.
  const closeModal = () => {
    setModalOpen(false);
    reload();
  };

  const isConnected = grants.length > 0;
  const mcpUrl = cloudApi.getMcpServerUrl();

  return (
    <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 p-6 flex flex-col transition-all duration-300 hover:shadow-xl">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Shown unmodified, used nominatively — same treatment as ClaudeMark. */}
          <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden shadow-md shadow-gray-200/30 dark:shadow-black/20 ring-1 ring-gray-200/80 dark:ring-gray-700/60 bg-white dark:bg-gray-800 flex items-center justify-center">
            <ChatGptMark className="w-full h-full" rounded="rounded-none" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">ChatGPT</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Scrape, query &amp; run automations by asking</p>
          </div>
        </div>

        {isConnected && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
            Connected
          </span>
        )}
      </div>

      {loading ? (
        <CardBodySkeleton />
      ) : isConnected ? (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
            ChatGPT can query your tables, clean up columns, run automations and launch new extractions —
            just by asking. Disconnecting revokes access immediately.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04]">
            {grants.map((grant) => (
              <ConnectedClientRow
                key={grant.id}
                grant={grant}
                onDisconnect={handleDisconnect}
                disconnecting={disconnecting === grant.id}
              />
            ))}
          </div>

          <div className="flex items-center justify-end gap-2 mt-auto pt-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              <HiOutlineArrowPath className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              Connect another
              <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            Turn ChatGPT into a web scraper: launch extractions, get clean tables back, then filter and
            tidy them — just by asking. No API keys; disconnect anytime.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          <div className="mt-auto">
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              Connect ChatGPT
              <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <SetupModal isOpen={modalOpen} onClose={closeModal} mcpUrl={mcpUrl} />
    </div>
  );
};

export default ChatGptIntegrationCard;
